/** @jsxImportSource @emotion/react */
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ITransaction } from '../../../../api/transactions/types';
import { ICharge } from '../../../../api/types';
import { energyRateLinesStyles } from './energy-rate-lines.styles';

type TariffPricesPeriod = {
    startHours: number;
    startMinutes: number;
    endHours: number;
    endMinutes: number;
    days: number[];
    price: number;
};

type TariffTieredPricesPeriod = {
    quantity: number;
    price: number;
};

type PricingLike = {
    currency?: string;
    type?: 'fixed' | 'tiered';
    pricingOption?: 'free' | 'kwh' | 'hour';
    tieredPricingOption?: 'kwh' | 'hour';
    priceForHour?: number;
    priceForKwh?: number;
    pricesPeriods?: TariffPricesPeriod[];
    tieredPricesPeriods?: TariffTieredPricesPeriod[];
};

type Props = {
    guest: ITransaction;
    charge: ICharge;
};

type Line = {
    label: string;
    value: string;
    status?: 'passed' | 'completed' | 'active' | 'next';
    isActive?: boolean;
};

const unitForPricingOption = (pricingOption?: string) => {
    if (pricingOption === 'hour') return 'h';
    return 'kWh';
};

const formatTime = (hours: number, minutes: number) => {
    const h = ((hours % 24) + 24) % 24;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hh = h % 12 === 0 ? 12 : h % 12;
    const mm = String(Math.max(0, Math.min(59, minutes))).padStart(2, '0');
    return `${hh}:${mm} ${ampm}`;
};

const toMinuteOfDay = (hours: number, minutes: number) => {
    const h = Math.max(0, Math.min(24, hours));
    const m = Math.max(0, Math.min(59, minutes));
    return Math.min(24 * 60, h * 60 + m);
};

type Segment = { startMin: number; endMin: number; price: number };

const buildSegmentsForDay = (periods: TariffPricesPeriod[], day: number, basePrice: number) => {
    const dayPeriods = (periods ?? [])
        .filter(p => Array.isArray(p.days) && p.days.includes(day))
        .map(p => ({
            startMin: toMinuteOfDay(p.startHours, p.startMinutes),
            endMin: toMinuteOfDay(p.endHours, p.endMinutes),
            price: p.price ?? 0,
        }))
        .filter(p => p.endMin > p.startMin)
        .sort((a, b) => a.startMin - b.startMin);

    const result: Segment[] = [];
    let cursor = 0;
    for (const p of dayPeriods) {
        const start = Math.max(0, Math.min(24 * 60, p.startMin));
        const end = Math.max(0, Math.min(24 * 60, p.endMin));
        if (start > cursor) result.push({ startMin: cursor, endMin: start, price: basePrice });
        result.push({ startMin: start, endMin: end, price: p.price });
        cursor = Math.max(cursor, end);
    }
    if (cursor < 24 * 60) result.push({ startMin: cursor, endMin: 24 * 60, price: basePrice });

    // merge adjacent
    const merged: Segment[] = [];
    for (const seg of result) {
        const prev = merged.at(-1);
        if (prev && prev.price === seg.price && prev.endMin === seg.startMin) prev.endMin = seg.endMin;
        else merged.push({ ...seg });
    }
    return merged;
};

const formatMinutesHM = (totalMinutes: number) => {
    const minutes = Math.max(0, Math.round(totalMinutes));
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h <= 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
};

const formatTierQuantityLabel = (qty: number, tieredPricingOption?: 'kwh' | 'hour') => {
    if (tieredPricingOption === 'hour') return formatMinutesHM(qty * 60);
    return `${qty} kWh`;
};

const formatTierRangeLabel = (from: number, to: number, tieredPricingOption?: 'kwh' | 'hour') => {
    if (tieredPricingOption === 'hour') {
        return `${formatMinutesHM(from * 60)}–${formatMinutesHM(to * 60)}`;
    }
    return `${from}–${to}`;
};

export const EnergyRateLines: FC<Props> = ({ guest, charge }) => {
    const intl = useIntl();
    const pricing = (guest?.pricing ?? {}) as unknown as PricingLike;
    const currency = pricing.currency ?? guest?.pricing?.currency ?? '';
    const pricingOption = pricing.pricingOption ?? 'kwh';
    const type = pricing.type ?? 'fixed';

    const now = new Date();
    const todayMon0 = (now.getDay() + 6) % 7; // Mon=0..Sun=6
    const nowMin = now.getHours() * 60 + now.getMinutes();

    const lines: Line[] = useMemo(() => {
        if (pricingOption === 'free') return [];

        const tieredPeriods = Array.isArray(pricing.tieredPricesPeriods) ? pricing.tieredPricesPeriods : [];
        const showTiered = type === 'tiered' && tieredPeriods.length > 0;

        const basePrice =
            pricingOption === 'hour' ? pricing.priceForHour ?? 0 : pricing.priceForKwh ?? 0;

        if (showTiered) {
            const sorted = [...tieredPeriods].sort((a, b) => (a.quantity ?? 0) - (b.quantity ?? 0));
            const perUnit = unitForPricingOption(pricingOption);

            const metric =
                pricing.tieredPricingOption === 'hour'
                    ? // elapsed minutes
                      Math.max(
                          0,
                          (Date.now() - new Date(charge.startAt).getTime()) / 1000 / 60
                      ) / 60 // convert minutes to "hours" quantity units
                    : // kWh delivered
                      Math.max(0, (charge.energy ?? 0) / 1000);

            const rows: Line[] = [];
            const firstStartQ = sorted?.[0]?.quantity ?? 0;

            // Before first tier start point, show base price (tariff priceForKwh/priceForHour).
            if (firstStartQ > 0) {
                const labelPart = formatTierQuantityLabel(firstStartQ, pricing.tieredPricingOption);
                const rangePart = ` (${formatTierRangeLabel(0, firstStartQ, pricing.tieredPricingOption)})`;
                const status: Line['status'] = metric < firstStartQ ? 'active' : 'completed';
                rows.push({
                    label: intl.formatMessage(
                        { id: 'tariffDetails.tiered.first' },
                        { segment: labelPart, range: rangePart }
                    ),
                    value: `${Number(basePrice / 100).toFixed(2)} ${currency}/${perUnit}`,
                    status,
                    isActive: status === 'active',
                });
            }

            // Tier rows: each tier applies from its start quantity up to the next tier start.
            if (sorted.length >= 2) {
                for (let i = 0; i < sorted.length - 1; i++) {
                    const startQ = sorted[i].quantity ?? 0;
                    const endQ = sorted[i + 1].quantity ?? startQ;
                    const delta = Math.max(0, endQ - startQ);

                    const isFirst = rows.length === 0;
                    const labelPart = formatTierQuantityLabel(delta, pricing.tieredPricingOption);
                    const rangePart = ` (${formatTierRangeLabel(startQ, endQ, pricing.tieredPricingOption)})`;

                    const status: Line['status'] =
                        metric >= endQ ? 'completed' : metric >= startQ ? 'active' : 'next';

                    rows.push({
                        label: intl.formatMessage(
                            { id: isFirst ? 'tariffDetails.tiered.first' : 'tariffDetails.tiered.next' },
                            { segment: labelPart, range: rangePart }
                        ),
                        value: `${Number(((sorted[i].price ?? 0) as number) / 100).toFixed(2)} ${currency}/${perUnit}`,
                        status,
                        isActive: status === 'active',
                    });
                }
            }

            if (sorted.length > 0) {
                const last = sorted.at(-1);
                const lastQ = last?.quantity ?? 0;
                const status: Line['status'] = metric >= lastQ ? 'active' : 'next';
                rows.push({
                    label: intl.formatMessage(
                        { id: 'tariffDetails.tiered.after' },
                        { quantity: formatTierQuantityLabel(lastQ, pricing.tieredPricingOption) }
                    ),
                    value: `${Number(((last?.price ?? 0) as number) / 100).toFixed(2)} ${currency}/${perUnit}`,
                    status,
                    isActive: status === 'active',
                });
            }

            // Show only the first "next" tier after active/completed as next, hide status for later
            let nextShown = false;
            return rows.map(r => {
                if (r.status !== 'next') return r;
                if (nextShown) return { ...r, status: undefined };
                nextShown = true;
                return r;
            });
        }

        const periods = Array.isArray(pricing.pricesPeriods) ? pricing.pricesPeriods : [];
        const unit = unitForPricingOption(pricingOption);

        if (periods.length === 0) {
            return [
                {
                    label: pricingOption === 'hour' ? 'Per hour' : 'Per kWh',
                    value: `${Number(basePrice / 100).toFixed(2)} ${currency}/${unit}`,
                },
            ];
        }

        const segments = buildSegmentsForDay(periods, todayMon0, basePrice);

        const withStatus: Line[] = segments.map(s => {
            const isActive = nowMin >= s.startMin && nowMin < s.endMin;
            const isPassed = nowMin >= s.endMin;
            return {
                label: `${formatTime(Math.floor(s.startMin / 60), s.startMin % 60)} – ${formatTime(
                    Math.floor(s.endMin / 60),
                    s.endMin % 60
                )}`,
                value: `${Number((s.price ?? 0) / 100).toFixed(2)} ${currency}/${unit}`,
                status: isActive ? 'active' : isPassed ? 'passed' : undefined,
                isActive,
            };
        });

        // Mark the first future segment as "next"
        const firstFuture = withStatus.findIndex(l => l.status === undefined && !l.isActive);
        const nextIndex = withStatus.findIndex(l => l.status === undefined && l.isActive !== true);
        const idx = firstFuture >= 0 ? firstFuture : nextIndex;
        if (idx >= 0 && withStatus[idx]) withStatus[idx] = { ...withStatus[idx], status: 'next' };

        // For UX similar to screenshots, show at most: last passed, active, next
        const activeIdx = withStatus.findIndex(l => l.status === 'active');
        if (activeIdx >= 0) {
            const lastPassedIdx = [...withStatus]
                .map((l, i) => ({ l, i }))
                .filter(x => x.l.status === 'passed')
                .at(-1)?.i;
            const nextIdx = withStatus.findIndex(l => l.status === 'next');

            const pick = new Set<number>();
            if (lastPassedIdx !== undefined) pick.add(lastPassedIdx);
            pick.add(activeIdx);
            if (nextIdx >= 0) pick.add(nextIdx);
            return withStatus.filter((_, i) => pick.has(i));
        }

        return withStatus;
    }, [
        charge.energy,
        charge.startAt,
        currency,
        nowMin,
        pricing.priceForHour,
        pricing.priceForKwh,
        pricing.pricesPeriods,
        pricing.tieredPricesPeriods,
        pricing.tieredPricingOption,
        pricingOption,
        todayMon0,
        type,
    ]);

    const title = useMemo(() => {
        const tieredPeriods = Array.isArray(pricing.tieredPricesPeriods) ? pricing.tieredPricesPeriods : [];
        const showTiered = type === 'tiered' && tieredPeriods.length > 0 && pricingOption !== 'free';
        return showTiered ? 'Energy tier progress' : 'Energy price schedule';
    }, [pricing.tieredPricesPeriods, pricingOption, type]);

    if (!lines.length) return null;

    return (
        <div css={energyRateLinesStyles.container}>
            <div css={energyRateLinesStyles.title}>{title}</div>
            <div css={energyRateLinesStyles.list}>
                {lines.map((l, idx) => (
                    <div key={idx} css={energyRateLinesStyles.row}>
                        <div css={energyRateLinesStyles.left}>
                            <span
                                css={[
                                    energyRateLinesStyles.dot,
                                    l.isActive ? energyRateLinesStyles.dotActive : null,
                                    l.status === 'completed'
                                        ? energyRateLinesStyles.dotCompleted
                                        : null,
                                ]}
                            />
                            <div css={energyRateLinesStyles.label}>
                                {l.label} {'\u2015'} {l.value}
                            </div>
                        </div>
                        <div css={energyRateLinesStyles.value}>{l.status && (
                                    <span
                                        css={[
                                            energyRateLinesStyles.status,
                                            l.isActive ? energyRateLinesStyles.statusActive : null,
                                            l.status === 'completed'
                                                ? energyRateLinesStyles.statusCompleted
                                                : null,
                                        ]}>
                                        {intl.formatMessage({
                                            id: `energyRate.status.${l.status}`,
                                            defaultMessage: l.status,
                                        })}
                                    </span>
                                )}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

